import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import Hamburger from '../Hamburger'
import smiley from '../../static/images/smiley.svg'
import { CONFIG } from '../../config'

import {
  NavbarContainer,
  NavbarInner,
  SubNav,
  LinksWrapper,
  NavLink,
} from './styles'

import { useAuth } from '../../hooks/useAuth'

const Links = ({ handleClick }) => (
  <NavLink>
    <Link
      className="homelink"
      onClick={handleClick}
      to="/"
      data-item-category="NV"
      data-item-name="home"
      data-testid="menu-home-link"
    >
      Home
    </Link>
    <Link
      className="link mobile-only"
      onClick={handleClick}
      to="/#plans"
      data-item-category="NV"
      data-item-name="sim-plans"
      data-testid="menu-sim-plans-link"
    >
      SIM plans
    </Link>
    <a
      className="link mobile-only"
      onClick={handleClick}
      href="https://help.smarty.co.uk/en/"
      rel="noopener noreferrer nofollow"
      target="_blank"
      data-item-category="NV"
      data-item-name="help-and-support"
      data-testid="menu-help-link"
    >
      Help &amp; support
    </a>
    <Link
      className="link"
      onClick={handleClick}
      to="/about-us"
      data-item-category="NV"
      data-item-name="about-us"
      data-testid="menu-about-link"
    >
      About
    </Link>
    <Link
      className="link"
      onClick={handleClick}
      to="/refer-a-friend"
      data-item-category="NV"
      data-item-name="refer-a-friend"
      data-testid="menu-raf-link"
    >
      Get a free month
    </Link>
    <Link
      className="link"
      onClick={handleClick}
      to="/group-plans"
      data-item-category="NV"
      data-item-name="group-plans"
      data-testid="menu-group-plans-link"
    >
      Group plans
    </Link>

    <Link
      className="link-tag link-tag-new"
      onClick={handleClick}
      to="/app"
      data-item-category="NV"
      data-item-name="app"
      data-testid="menu-app-link"
    >
      App
    </Link>
  </NavLink>
)

Links.propTypes = {
  handleClick: PropTypes.func,
}

const ActionLinks = () => {
  const isAuthenticated = useAuth()
  if (isAuthenticated) {
    return (
      <NavLink>
        <a
          className="dashboardlink"
          href={`${CONFIG.DASHBOARD_URL}/dashboard`}
          data-item-category="NV"
          data-item-name="go-to-dashboard"
          data-testid="menu-dashboard-button"
        >
          <img src={smiley} alt="" /> Go to my dashboard
        </a>
      </NavLink>
    )
  }
  return (
    <NavLink isRow>
      <a
        className="loginlink"
        href={`${CONFIG.DASHBOARD_URL}/dashboard`}
        data-item-category="NV"
        data-item-name="log-in"
        data-testid="menu-login-button"
      >
        <img src={smiley} alt="" /> Log in
      </a>
      <a
        className="activatelink"
        href="/activate"
        data-item-category="UA"
        data-item-name="activate-sim"
        data-testid="menu-activate-button"
      >
        Activate SIM
      </a>
    </NavLink>
  )
}

const Navbar = () => {
  const [navbarOpen, setNavbarOpen] = useState(false)
  const KEYCODE_TAB = 9
  const handleResize = useCallback(() => {
    if (window.innerWidth > 850) setNavbarOpen(false)
  })

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  })

  const toggleNav = () => {
    setNavbarOpen((existing) => !existing)
  }

  const closeNav = () => {
    setNavbarOpen(false)
  }

  const handleKeydown = (e) => {
    const navEl = document.getElementById('nav')
    const focusable = navEl.querySelectorAll(
      'button, [href], input, select, textarea, li, a,[tabindex]:not([tabindex="-1"])'
    )
    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]
    const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB

    if (!isTabPressed || !navbarOpen) {
      return
    }

    if (e.shiftKey) {
      /* shift + tab */ if (document.activeElement === firstFocusable) {
        lastFocusable.focus()
        e.preventDefault()
      }
    } /* tab */ else {
      /* eslint-disable no-lonely-if */
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus()
        e.preventDefault()
      }
      /* eslint-enable no-lonely-if */
    }
  }

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0]
    if (navbarOpen) {
      body.style.overflow = 'hidden'
    } else {
      body.style.overflow = 'unset'
      window.removeEventListener('keydown', handleKeydown)
    }
  }, [navbarOpen])

  return (
    <NavbarContainer
      id="nav"
      role="navigation"
      data-testid="navbar-container"
      navbarOpen={navbarOpen}
    >
      <NavbarInner navbarOpen={navbarOpen}>
        <Link
          className="logo"
          to="/"
          data-item-category="NV"
          data-item-name="home"
          data-testid="navbar-logo-link"
          onClick={navbarOpen ? toggleNav : null}
        >
          Home
        </Link>
        <Hamburger open={navbarOpen} onClick={toggleNav} />
        <SubNav navbarOpen={navbarOpen} data-testid="subnav">
          <LinksWrapper hideLinks={!navbarOpen}>
            <ActionLinks />
            <Links handleClick={closeNav} />
          </LinksWrapper>
        </SubNav>
      </NavbarInner>
    </NavbarContainer>
  )
}

export default Navbar
